import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  ILocalTimeRange,
  LocalAvailableTime,
} from '../../../models/schedule-calendar.models';
import { timeRangeOrderValidator } from '../../../validators/schedule-calendar.validators';

export type RangeFormGroup = FormGroup<{
  start: FormControl<string>;
  end: FormControl<string>;
}>;

/** The available time, and the parts of it that are preferred */
export interface ITimesControls {
  time: RangeFormGroup;
  allPreferred: FormControl<boolean>;
  preferredTimes: FormArray<RangeFormGroup>;
}

export interface ITimesValue {
  time: ILocalTimeRange;
  allPreferred: boolean;
  preferredTimes: ILocalTimeRange[];
}

export const createTimeRangeGroup = ({
  start,
  end,
}: ILocalTimeRange): RangeFormGroup =>
  new FormGroup(
    {
      start: new FormControl(start),
      end: new FormControl(end),
    },
    timeRangeOrderValidator()
  );

/**
 * Preferred ranges are disabled while all the time is preferred, and are
 * clamped to the available time whenever it changes.
 *
 * The subscriptions are to the controls' own changes, so they're released
 * along with the controls.
 */
export const createTimesControls = ({
  time,
  allPreferred,
  preferredTimes,
}: ITimesValue): ITimesControls => {
  const controls: ITimesControls = {
    time: createTimeRangeGroup(time),
    allPreferred: new FormControl(allPreferred),
    preferredTimes: new FormArray(preferredTimes.map(createTimeRangeGroup)),
  };

  if (allPreferred) {
    controls.preferredTimes.disable();
  }

  controls.allPreferred.valueChanges.subscribe((isAllPreferred) =>
    isAllPreferred
      ? controls.preferredTimes.disable()
      : controls.preferredTimes.enable()
  );

  controls.time.valueChanges.subscribe(() =>
    clampPreferredTimesToParentTimeRange(controls)
  );

  return controls;
};

const clampPreferredTimesToParentTimeRange = ({
  time,
  preferredTimes,
}: ITimesControls) => {
  if (time.invalid) {
    return;
  }

  const { start, end } = time.getRawValue();

  // iterate backwards so removeAt doesn't shift unvisited indices
  for (let i = preferredTimes.length - 1; i >= 0; i--) {
    const range = preferredTimes.at(i).getRawValue();
    const clampedStart = range.start < start ? start : range.start;
    const clampedEnd = range.end > end ? end : range.end;

    if (clampedStart >= clampedEnd) {
      preferredTimes.removeAt(i, { emitEvent: false });
      continue;
    }

    preferredTimes
      .at(i)
      .setValue({ start: clampedStart, end: clampedEnd }, { emitEvent: false });
  }

  preferredTimes.updateValueAndValidity();
};

/**
 * Splits the available time into consecutive periods, flagging those covered
 * by the preferred ranges, or all of it when it's all preferred.
 */
export const toAvailableTimes = ({
  time,
  allPreferred,
  preferredTimes,
}: ITimesValue): LocalAvailableTime[] =>
  allPreferred
    ? [{ ...time, isPreferred: true }]
    : splitByPreferredTimes(time, mergePreferredTimes(preferredTimes));

/**
 * Splits `time` into consecutive blocks, flagging those covered by
 * `preferred`, which must be sorted, non-overlapping and within `time`.
 */
const splitByPreferredTimes = (
  time: ILocalTimeRange,
  preferred: ILocalTimeRange[]
) => {
  const { blocks, cursor } = preferred.reduce<{
    blocks: LocalAvailableTime[];
    cursor: string;
  }>(
    (acc, { start, end }) => ({
      blocks: [
        ...acc.blocks,
        ...(start > acc.cursor
          ? [{ start: acc.cursor, end: start, isPreferred: false }]
          : []),
        { start, end, isPreferred: true },
      ],
      cursor: end,
    }),
    {
      blocks: [],
      cursor: time.start,
    }
  );

  return [
    ...blocks,
    ...(cursor < time.end
      ? [{ start: cursor, end: time.end, isPreferred: false }]
      : []),
  ];
};

const mergePreferredTimes = (times: ILocalTimeRange[]) =>
  [...times]
    .sort((a, b) => a.start.localeCompare(b.start))
    .reduce<ILocalTimeRange[]>((acc, time) => {
      const last = acc.at(-1);

      if (!last || time.start > last.end) {
        return [...acc, time];
      }

      return [
        ...acc.slice(0, -1),
        { start: last.start, end: time.end > last.end ? time.end : last.end },
      ];
    }, []);
